import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Button, Mic } from './primitives';
import { useCoachReviewMode } from '../hooks/useCoachReviewMode';
import { useVoiceToText } from '../lib/voiceInput';

/**
 * Per-item feedback prompt for pro-coach reviewers (Ben et al).
 *
 * Rendered on DrillDetail + SessionDetail. Self-gates: renders nothing
 * unless the current user is in review mode, so ordinary coaches never
 * see it. Voice-first — each prompt has a mic so a non-technical coach
 * can just speak — but typing works too. On save it writes a structured
 * row to `coach_review_log` (kind='manual_feedback' + entity/reaction
 * columns from migration 00090), which surfaces in the admin review feed
 * alongside the coach's free voice utterances.
 */

type Reaction = 'works' | 'mixed' | 'change';

const REACTIONS: { value: Reaction; emoji: string; label: string }[] = [
  { value: 'works', emoji: '👍', label: 'Works' },
  { value: 'mixed', emoji: '😐', label: 'Mixed' },
  { value: 'change', emoji: '👎', label: 'Change it' },
];

interface Props {
  entityType: 'DRILL' | 'SESSION';
  entityId: string;
  entityName: string;
}

export function ReviewFeedbackCard({ entityType, entityId, entityName }: Props): React.JSX.Element | null {
  const review = useCoachReviewMode();
  const [reaction, setReaction] = useState<Reaction | null>(null);
  const [works, setWorks] = useState('');
  const [change, setChange] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [activeField, setActiveField] = useState<'works' | 'change' | null>(null);
  const lastVoiceLenRef = useRef(0);

  // Single recogniser shared by both prompts (one mic on the device).
  // The final transcript is routed to whichever field's mic is active.
  const voice = useVoiceToText({
    onTranscript: (text) => {
      const delta = text.slice(lastVoiceLenRef.current);
      lastVoiceLenRef.current = text.length;
      const piece = delta.trim();
      if (!piece) return;
      if (activeField === 'works') setWorks((p) => (p ? `${p} ${piece}` : piece));
      else if (activeField === 'change') setChange((p) => (p ? `${p} ${piece}` : piece));
    },
  });
  useEffect(() => {
    if (!voice.listening) lastVoiceLenRef.current = 0;
  }, [voice.listening]);

  // Reset when the coach navigates to a different drill/session.
  useEffect(() => {
    setReaction(null);
    setWorks('');
    setChange('');
    setDone(false);
    setActiveField(null);
  }, [entityId]);

  const micFor = useCallback((field: 'works' | 'change') => {
    // One field at a time — if we're already listening, a tap stops it.
    // The coach taps again to dictate into the other field (clean handoff,
    // no overlapping recognisers).
    if (voice.listening) {
      voice.stop();
      return;
    }
    setActiveField(field);
    lastVoiceLenRef.current = 0;
    void voice.start();
  }, [voice]);

  const canSave = !!reaction || works.trim().length > 0 || change.trim().length > 0;

  const submit = useCallback(async () => {
    if (saving || !canSave) return;
    setSaving(true);
    const id = await review.logItemFeedback({
      entityType,
      entityId,
      entityName,
      reaction: reaction ?? 'mixed',
      whatWorks: works,
      whatToChange: change,
    });
    setSaving(false);
    if (id) {
      setDone(true);
      setReaction(null);
      setWorks('');
      setChange('');
      window.setTimeout(() => setDone(false), 2800);
    }
  }, [saving, canSave, review, entityType, entityId, entityName, reaction, works, change]);

  // Hold off until the review flag has hydrated, then gate to review mode.
  if (review.loading || !review.isReviewMode) return null;

  const noun = entityType === 'DRILL' ? 'drill' : 'session';

  return (
    <Card variant="opaque">
      <div style={cardInner}>
        <div style={headRow}>
          <span style={kicker}>Your feedback</span>
          <span style={kickerNote}>goes straight to Peter</span>
        </div>
        <p style={lead}>
          What do you make of this {noun}? Tap a face, then say or type a line or two — I'm listening.
        </p>

        <div style={reactionRow} role="group" aria-label="Quick reaction">
          {REACTIONS.map((r) => {
            const active = reaction === r.value;
            return (
              <button
                key={r.value}
                type="button"
                onClick={() => setReaction(active ? null : r.value)}
                aria-pressed={active}
                style={{
                  ...reactionChip,
                  ...(active ? reactionChipActive : null),
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 18 }}>{r.emoji}</span>
                {r.label}
              </button>
            );
          })}
        </div>

        <Field
          label="What works?"
          value={works}
          onChange={setWorks}
          placeholder="What's good about it as it is?"
          listening={voice.listening && activeField === 'works'}
          onMic={() => micFor('works')}
        />
        <Field
          label="What would you change?"
          value={change}
          onChange={setChange}
          placeholder="Anything you'd tweak, add, or drop?"
          listening={voice.listening && activeField === 'change'}
          onMic={() => micFor('change')}
        />

        {voice.status && <span style={voiceStatus}>{voice.status}</span>}

        <div style={footRow}>
          <Button variant="primary" size="workbench" onClick={() => void submit()} disabled={saving || !canSave}>
            {saving ? 'Sending…' : 'Send feedback'}
          </Button>
          {done && <span style={doneMsg} role="status" aria-live="polite">Thanks — sent to Peter ✓</span>}
        </div>
      </div>
    </Card>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  listening: boolean;
  onMic: () => void;
}

function Field({ label, value, onChange, placeholder, listening, onMic }: FieldProps): React.JSX.Element {
  return (
    <div style={fieldWrap}>
      <div style={fieldHead}>
        <span style={fieldLabel}>{label}</span>
        <Mic
          listening={listening}
          mode="toggle"
          onStart={onMic}
          onStop={onMic}
          aria-label={listening ? `Stop dictating ${label}` : `Speak your answer to: ${label}`}
          title={listening ? 'Stop dictating' : 'Tap and speak'}
        />
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder={placeholder}
        style={textarea}
      />
    </div>
  );
}

// ---- styles (design tokens) ------------------------------------------------
const cardInner: React.CSSProperties = {
  padding: 'var(--space-3, 16px)',
  borderLeft: '3px solid var(--accent, #27AE60)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const headRow: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' };
const kicker: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  fontWeight: 700,
  color: 'var(--accent, #27AE60)',
};
const kickerNote: React.CSSProperties = { fontSize: 11, opacity: 0.6 };
const lead: React.CSSProperties = { margin: 0, fontSize: 14, lineHeight: 1.5, opacity: 0.9 };
const reactionRow: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap' };
const reactionChip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 600,
  color: 'inherit',
  background: 'var(--surface-2, rgba(255,255,255,0.06))',
  border: '1px solid var(--glass-border, rgba(255,255,255,0.18))',
  borderRadius: 999,
  cursor: 'pointer',
};
const reactionChipActive: React.CSSProperties = {
  background: 'var(--accent, #27AE60)',
  borderColor: 'var(--accent, #27AE60)',
  color: '#06210f',
};
const fieldWrap: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
const fieldHead: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 };
const fieldLabel: React.CSSProperties = { fontSize: 12, fontWeight: 600, opacity: 0.85 };
const textarea: React.CSSProperties = {
  width: '100%',
  fontSize: 14,
  lineHeight: 1.5,
  padding: 10,
  background: 'var(--surface-1, rgba(255,255,255,0.05))',
  color: 'inherit',
  border: '1px solid var(--glass-border, rgba(255,255,255,0.18))',
  borderRadius: 8,
  resize: 'vertical',
  fontFamily: 'inherit',
};
const voiceStatus: React.CSSProperties = { fontSize: 11, opacity: 0.7 };
const footRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, marginTop: 2 };
const doneMsg: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: 'var(--state-good, #27AE60)' };
